import { type EventTypeAttributes } from '@/Views/FormSteps/EventType/Base/Fieldset'
import { type TimeOptionInput } from '@/Views/FormSteps/DateTime/TimePicker/TimeStamps/timeStamps'
import { type PreviousEventRequest } from '@/Components/RepeatEvent/PreviousEventCard'
import { type LocationSelectionData } from '@/Components/PreferredLocation/LocationSection'

export const DRAFT_TTL_MS = 30 * 60 * 1000

export interface DuplicateFormDraft {
  eventId: string | undefined
  eventName: string
  numGuests: number | ''
  eventTypeAttrs: EventTypeAttributes
  styleIds: string[]
  layout: string | undefined
  facilityIds: number[]
  cateringNeeds: string
  budget: number | ''
  additionalNotes: string
  dateStart: string | null
  dateEnd: string | null
  startTime: TimeOptionInput
  endTime: TimeOptionInput
  isFlexibleOnDate: boolean
  isFlexibleOnTime: boolean
  flexibilityNotes: string
  requestedVenues: PreviousEventRequest[]
  initialLocationData: LocationSelectionData | null
}

interface StoredDraft<T> {
  expiresAt: number
  data: T
}

export function createDraftStorage<T>(key: string, ttlMs: number = DRAFT_TTL_MS) {
  function save(data: T): void {
    const stored: StoredDraft<T> = { expiresAt: Date.now() + ttlMs, data }
    localStorage.setItem(key, JSON.stringify(stored))
  }

  function load(validate?: (data: T) => boolean): T | null {
    const raw = localStorage.getItem(key)
    if (!raw) return null
    try {
      const stored = JSON.parse(raw) as StoredDraft<T>
      if (Date.now() > stored.expiresAt) {
        localStorage.removeItem(key)
        return null
      }
      if (validate && !validate(stored.data)) {
        localStorage.removeItem(key)
        return null
      }
      return stored.data
    } catch {
      return null
    }
  }

  function clear(): void {
    localStorage.removeItem(key)
  }

  return { save, load, clear }
}

const duplicateStorage = createDraftStorage<DuplicateFormDraft>('hb_duplicate_form_draft')

export function saveDuplicateFormDraft(draft: DuplicateFormDraft): void {
  duplicateStorage.save(draft)
}

export function loadDuplicateFormDraft(eventId: string | undefined): DuplicateFormDraft | null {
  if (eventId === undefined) return null
  return duplicateStorage.load((data) => data.eventId === eventId)
}

export function clearDuplicateFormDraft(): void {
  duplicateStorage.clear()
}
